import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCartStore } from '../../store/cartStore';
import { useAuthStore } from '../../store/authStore';
import { usePaymentCheckout, CheckoutStateKind } from '../../hooks/usePaymentCheckout';
import useCheckoutQuote from '../../hooks/useCheckoutQuote';
import { useApiQuery } from '../../hooks/useApiQuery';
import { addressApi } from '../../lib/addressApi';
import { CommerceConfig, Payer } from '../../types/commerce';
import { DeliveryAddress } from '../../types/api';
import AddressPicker from './AddressPicker';
import CheckoutItems from './CheckoutItems';
import OrderSummary from './OrderSummary';
import PayerForm from './PayerForm';
import './CheckoutPage.css';

export default function CheckoutPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const auth = useAuthStore((s) => s.status);
  const items = useCartStore((s) => s.items);
  const clearCart = useCartStore((s) => s.clearCart);
  const completePurchase = useCartStore((s) => s.completePurchase);
  const [addresses, setAddresses] = useState<readonly DeliveryAddress[]>([]);
  const [chosenAddress, setChosenAddress] = useState('');
  const address = addresses.some((a) => a.id === chosenAddress)
    ? chosenAddress
    : (addresses.find((a) => a.is_default)?.id ?? addresses[0]?.id ?? '');
  const [editingAddress, setEditingAddress] = useState(false);
  // Only fields the shopper edited are stored; the rest default to the signed-in profile.
  const [payerEdits, setPayerEdits] = useState<Partial<Payer>>({});
  const payer: Payer = {
    name: payerEdits.name ?? user?.first_name ?? '',
    email: payerEdits.email ?? user?.email ?? '',
    phone: payerEdits.phone ?? user?.phone_number ?? '',
  };
  const [points, setPoints] = useState(0);
  const [error, setError] = useState('');
  const { data: config, error: configError } = useApiQuery<CommerceConfig>('/commerce/config');
  const payment = usePaymentCheckout(auth === 'authed' ? user?.id : undefined);
  const cartAtPayment = useRef('');
  const quote = useCheckoutQuote({
    items,
    addressId: address,
    payer,
    points,
    enabled: auth === 'authed' && !!config?.enabled,
    frozen: payment.blocked,
    // Refresh once for each authoritative release, including reload recovery.
    refreshKey: payment.retryGeneration,
  });

  const loadAddresses = useCallback(() => addressApi.list().then(setAddresses), []);
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return;
    loadAddresses().catch((e) => setError(e.message));
  }, [userId, loadAddresses]);

  useEffect(() => {
    if (payment.state.kind === CheckoutStateKind.Success) {
      if (JSON.stringify(useCartStore.getState().items) === cartAtPayment.current) completePurchase();
      if (payment.state.order.sales_order_id) navigate(`/orders/${payment.state.order.sales_order_id}`);
    }
  }, [payment.state, completePurchase, navigate]);

  const handleAddressSaved = async (id: string) => {
    await loadAddresses();
    setChosenAddress(id);
    quote.refresh();
  };

  const pay = async () => {
    if (!quote.quote) return;
    cartAtPayment.current = JSON.stringify(items);
    await payment.start({
      quote_id: quote.quote.id,
      items: items.map(({ inventory_id, quantity }) => ({
        inventory_id,
        quantity,
      })),
      delivery_address_id: address,
      email: quote.quote.payer.email,
      phone: quote.quote.payer.phone,
    });
  };

  return (
    <section className="commerce-checkout page-container">
      <h1>Checkout</h1>
      <button disabled={payment.blocked || !items.length} onClick={clearCart}>
        Clear cart
      </button>
      {auth === 'guest' ? (
        <p>
          <Link to="/login?next=/checkout">Sign in to continue checkout</Link>
        </p>
      ) : null}
      {(error || configError) && <p role="alert">{error || configError}</p>}
      {!items.length ? (
        <p>
          Your bag is empty. <Link to="/">Continue shopping</Link>
        </p>
      ) : (
        <div className="checkout-columns">
          <div>
            <CheckoutItems quotedLines={quote.quote?.items} locked={payment.blocked} />
            {auth === 'authed' && (
              <>
                <AddressPicker
                  addresses={addresses}
                  selectedId={address}
                  onSelect={setChosenAddress}
                  locked={payment.blocked}
                  editing={editingAddress}
                  onEditingChange={setEditingAddress}
                  onSaved={handleAddressSaved}
                  onError={setError}
                />
                <PayerForm
                  payer={payer}
                  locked={payment.blocked}
                  onChange={(key, value) => setPayerEdits((edits) => ({ ...edits, [key]: value }))}
                />
              </>
            )}
          </div>
          <OrderSummary
            config={config}
            quote={quote.quote}
            quoteBusy={quote.busy}
            quoteError={quote.error}
            points={points}
            onPointsChange={setPoints}
            paymentState={payment.state}
            paymentBlocked={payment.blocked}
            onCheckStatus={() => void payment.checkStatus()}
            onReleaseAndRetry={() => void payment.releaseAndRetry()}
            canPay={!editingAddress}
            onPay={() => void pay()}
          />
        </div>
      )}
    </section>
  );
}
